import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';

interface DistributionChartProps {
  distribution: Record<string, number>;
  labels?: Record<string, string>;
}

function formatLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function DistributionChart({ distribution, labels }: DistributionChartProps) {
  const navigate = useNavigate();
  const entries = Object.entries(distribution).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm text-slate-400 text-center">
        No classified entities yet. Run the OntologicalReasoningAgent to populate.
      </div>
    );
  }

  const max = Math.max(...entries.map(([, n]) => n));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-navy-900">Class Distribution</h3>
        <span className="text-[10px] text-slate-400">
          click a bar to filter Entities
        </span>
      </div>
      <div className="space-y-1.5">
        {entries.map(([key, count]) => {
          const pct = max > 0 ? (count / max) * 100 : 0;
          return (
            <button
              key={key}
              onClick={() =>
                navigate(`/knowledge/estate/entities?uos_class=${encodeURIComponent(key)}`)
              }
              className="w-full flex items-center gap-3 text-left group hover:bg-slate-50 rounded-lg px-2 py-1 transition-colors"
            >
              <span className="text-xs text-slate-600 w-44 truncate">
                {labels?.[key] ?? formatLabel(key)}
              </span>
              <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                <div
                  className={clsx(
                    'h-full rounded-full transition-all duration-500 group-hover:bg-brand-teal-dark',
                    'bg-brand-teal',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-navy-900 w-12 text-right">
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
